import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';

import { distinctUntilChanged, filter, map, Subject, takeUntil } from 'rxjs';

import { Availability } from '../../../core/enums/availability.enum';
import { OrderMenuItemSourceType } from '../../../core/enums/order-menu-item-source-type.enum';

import { Badge } from '../../../core/models/badge.model';
import { MarketingCollectionDetailsProduct } from '../../../core/models/marketing-collection-details.model';
import { MultiLanguageValue } from '../../../core/models/multi-language-value.model';
import { NewCartItem } from '../../../core/models/new-cart-item.model';
import { VendorDetails, VendorMenuItem } from '../../../core/models/vendor-details.model';

import { AnalyticsService } from '../../../core/services/analytics.service';
import { CartService } from '../../../core/services/cart.service';
import { SettingsService } from '../../../core/services/settings.service';
import { VendorsService } from '../../../core/services/vendors.service';

import { AnalyticsConfig, TOKEN_ANALYTICS_CONFIG } from '../../../core/config/analytics.config';

import { Helpers } from '../../../core/helpers';

import { MenuItemComponentAnimations } from './menu-item.animations';

declare type ItemInput = VendorMenuItem | MarketingCollectionDetailsProduct;

interface ItemDetails {
  itemId: number;
  vendorId: number;
  vendorName: string;
  vendorLogoUrl: string;
  name: string;
  nameJson: string;
  description: string;
  badges?: Array<Badge>;
  availability: Availability;
  availabilityText?: string;
  availabilityTextColor?: string;
  imageUrl: string;
  noticePeriod: number;
  noticePeriodWarningMessage?: string;
  price: number;
  retailPrice: number;
  supportStartingPrice?: boolean;
  minimumCost?: number;
  retailMinimumCost?: number;
  onSale: boolean;
  maxQuantity: number;
  minQuantity: number;
  quantityIncrement: number;
  serviceType: number;
  minimumOrderValue: number;
  supportsQuickAddToCart: boolean;
  supportsCheckNextAvailableDateTime: boolean;
}

@Component({
  selector: 'app-menu-item',
  templateUrl: './menu-item.component.html',
  styleUrls: ['./menu-item.component.scss'],
  animations: [...MenuItemComponentAnimations],
  encapsulation: ViewEncapsulation.None,
})
export class MenuItemComponent implements OnInit, OnDestroy {
  @Input()
  public vendorDetails?: VendorDetails;

  @Input()
  public quickAddToCart: boolean;

  @Input()
  public showVendorName: boolean;

  @Output()
  public itemSelected = new EventEmitter<void>();

  @Output()
  public vendorSelected = new EventEmitter<void>();

  @Output()
  public itemIncremented = new EventEmitter<void>();

  @Output()
  public itemDecremented = new EventEmitter<void>();

  @Output()
  public itemAvailabilityRequested = new EventEmitter<void>();

  public itemDetails: ItemDetails;
  public itemQuantity: number;

  public Availability = Availability;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private cartService: CartService,
    private vendorsService: VendorsService,
    private settingsService: SettingsService,
    private analyticsService: AnalyticsService,
    @Inject(TOKEN_ANALYTICS_CONFIG) private analyticsConfig: AnalyticsConfig,
  ) {}

  @Input()
  public set item(item: ItemInput) {
    this.itemDetails = this.getItemDetails(item);
  }

  ngOnInit() {
    // This will both initialize the item quantity and keep it
    // updated as the cart changes
    this.cartService.cart$
      .pipe(
        filter(() => !!this.itemDetails),
        map(() => this.cartService.getItemQuantityByVendor(this.itemDetails.vendorId, this.itemDetails.itemId)),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((newQuantity) => {
        if (this.itemQuantity !== newQuantity) {
          this.itemQuantity = newQuantity;
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public onDecreaseItemQuantity(event: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.itemDetails.supportsQuickAddToCart) {
      this.itemDecremented.emit();
      return;
    }

    void this.maybeUpdateMenuItemQuantity(this.itemQuantity - 1);
  }

  public onIncreaseItemQuantity(event: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.itemDetails.supportsQuickAddToCart) {
      this.itemIncremented.emit();
      return;
    }

    const newItemQuantity = this.itemQuantity ? this.itemQuantity + this.itemDetails.quantityIncrement : this.itemDetails.minQuantity;
    void this.maybeUpdateMenuItemQuantity(newItemQuantity).then((wasAdded) => {
      if (wasAdded) {
        this.trackItemAddedCleverTapEvent();
      }
    });
  }

  public onItemSelected(): void {
    this.itemSelected.emit();
  }

  public onVendorSelected(event: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.vendorSelected.emit();
  }

  public onCheckAvailability(event: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.itemAvailabilityRequested.emit();
  }

  private maybeUpdateMenuItemQuantity(newItemQuantity: number): Promise<boolean> {
    const newItemTotal = Helpers.multiplyWithPrecision(this.itemDetails.price, newItemQuantity);

    if (newItemQuantity === 0) {
      this.cartService.removeItemsFromCart(this.itemDetails.vendorId, [this.itemDetails.itemId]);
      return Promise.resolve(true);
    }

    const newCartItem: NewCartItem = {
      catererId: this.itemDetails.vendorId,
      catererName: this.itemDetails.vendorName,
      menuItemId: this.itemDetails.itemId,
      menuItemName: this.itemDetails.name,
      serviceType: this.itemDetails.serviceType,
      sourceId: this.itemDetails.vendorId,
      sourceType: OrderMenuItemSourceType.QuickAddToCartVendor,
      imageUrl: this.itemDetails.imageUrl,
      minimumOrderValue: this.itemDetails.minimumOrderValue,
      noticePeriod: this.itemDetails.noticePeriod,
      quantity: newItemQuantity,
      total: newItemTotal,
    };

    return this.cartService
      .maybeAddToCart({
        item: newCartItem,
        address: this.vendorsService.getAddressSearchFilters(),
      })
      .then(({ added }) => {
        void this.analyticsService.trackEvent({
          name: this.analyticsConfig.eventName.menuItemDetailsPageAddToCart,
          data: {
            [this.analyticsConfig.propertyName.catererId]: this.itemDetails.vendorId,
            [this.analyticsConfig.propertyName.menuItemId]: this.itemDetails.itemId,
            [this.analyticsConfig.propertyName.amount]: this.itemDetails.price,
            [this.analyticsConfig.propertyName.currency]: this.settingsService.currencySymbolEN,
            [this.analyticsConfig.propertyName.isGroceryItem]: false,
          },
          includeAreaDateTime: true,
        });

        return added;
      });
  }

  private trackItemAddedCleverTapEvent(): void {
    const itemName = this.itemDetails.nameJson ? (JSON.parse(this.itemDetails.nameJson) as MultiLanguageValue) : null;

    this.analyticsService.trackItemAddedCleverTapEvent({
      id: this.itemDetails.itemId,
      nameEn: itemName ? Helpers.getLocalizedMultiLanguageValue(itemName, 'en') : null,
      nameAr: itemName ? Helpers.getLocalizedMultiLanguageValue(itemName, 'ar') : null,
      currency: this.settingsService.currencySymbolEN,
      amount: this.itemDetails.price,
      vendorId: this.itemDetails.vendorId,
      serviceType: this.itemDetails.serviceType,
    });
  }

  private isItemFromMarketingCollection(item: ItemInput): item is MarketingCollectionDetailsProduct {
    return !!(item as Pick<MarketingCollectionDetailsProduct, 'productId'>).productId;
  }

  private getItemDetailsBasedOnMarketingCollectionItem(item: MarketingCollectionDetailsProduct): ItemDetails {
    return {
      itemId: item.productId,
      vendorId: item.vendorId,
      vendorName: item.vendorName,
      vendorLogoUrl: undefined,
      imageUrl: item.imageUrl,
      name: item.name,
      nameJson: null,
      description: item.description,
      badges: item.badges,
      price: item.price,
      onSale: item.onSale,
      retailPrice: item.retailPrice,
      supportStartingPrice: item.supportStartingPrice,
      minimumCost: item.minimumCost,
      retailMinimumCost: item.retailMinimumCost,
      availability: Availability.Available,
      availabilityText: null,
      availabilityTextColor: null,
      noticePeriod: null,
      noticePeriodWarningMessage: null,
      serviceType: item.serviceType,
      supportsQuickAddToCart: false,
      supportsCheckNextAvailableDateTime: false,
      minQuantity: undefined,
      maxQuantity: undefined,
      quantityIncrement: undefined,
      minimumOrderValue: undefined,
    };
  }

  // IMPORTANT: for now we'll only support the quick-add-to-cart feature in the
  // vendor details page (and local search) because we need some properties that
  // we only have there and not in the marketing details page or the items home
  // page for example

  private getItemDetailsBasedOnVendorMenuItem(item: VendorMenuItem): ItemDetails {
    const supportsQuickAddToCart = this.settingsService.isQuickAddToCartFeatureEnabled() && !item.optionsCount && !item.addOnsCount;
    const supportsCheckNextAvailableDateTime = item.supportNextAvailable || false;

    return {
      itemId: item.menuItemId,
      vendorId: item.vendorId,
      vendorName: this.vendorDetails ? this.vendorDetails.name : undefined,
      vendorLogoUrl: this.vendorDetails ? this.vendorDetails.logoUrl : undefined,
      imageUrl: item.imageUrl,
      name: item.name,
      nameJson: item.nameJson,
      description: item.description,
      badges: item.badges,
      price: item.price,
      onSale: item.onSale,
      retailPrice: item.retailPrice,
      supportStartingPrice: item.supportStartingPrice,
      minimumCost: item.minimumCost,
      retailMinimumCost: item.retailMinimumCost,
      availability: item.availability,
      availabilityText: item.availabilityText,
      availabilityTextColor: item.availabilityTextColor,
      noticePeriod: item.noticePeriod,
      noticePeriodWarningMessage: item.noticePeriodWarningMessage,
      serviceType: item.serviceType,
      supportsQuickAddToCart,
      supportsCheckNextAvailableDateTime,
      maxQuantity: supportsQuickAddToCart ? item.maxQuantity ?? 0 : undefined,
      minQuantity: supportsQuickAddToCart ? item.minQuantity ?? 1 : undefined,
      quantityIncrement: supportsQuickAddToCart ? item.quantityIncrement ?? 1 : undefined,
      minimumOrderValue: this.vendorDetails
        ? this.vendorDetails.serviceTypeRequirements?.find((req) => req.serviceType === item.serviceType)?.minimumOrderAmount
        : undefined,
    };
  }

  private getItemDetails(item: ItemInput): ItemDetails {
    return this.isItemFromMarketingCollection(item)
      ? this.getItemDetailsBasedOnMarketingCollectionItem(item)
      : this.getItemDetailsBasedOnVendorMenuItem(item);
  }
}
